/**
 * The floor a body stands on, as a basis you can draw in.
 *
 * The pedestal sprite cannot be made to lie in this floor. It is a solid object
 * rendered once at cos(68deg), and a cell here is up to 2.8x deeper and shears by
 * 30 degrees; warping the sprite to fit destroys the very thing that reads as a
 * machined plate. Distorted metal looks broken in a way that a slightly wrong
 * ellipse never does, so the plate keeps its own form and its own proportions.
 *
 * What ties it to the terrain instead is its contact shadow and the light it throws
 * back down. Those are soft, shapeless things — they can take the cell's exact
 * rotation, depth and shear without anyone being able to tell they were stretched,
 * and a shadow lying correctly in the floor is what makes the object above it read
 * as standing on the ground rather than pasted over it.
 *
 * Everything here comes from the two vectors the cell spans, so it follows whatever
 * grid settings, cell size and corner morph a map was authored with, at any footprint.
 */

export interface Point {
  x: number;
  y: number;
}

/** The morphed corners of the cell block a body stands on, in overlay pixels. */
export interface CellQuad {
  topLeft: Point;
  topRight: Point;
  bottomRight: Point;
  bottomLeft: Point;
}

/**
 * A canvas transform mapping the unit circle onto the cell's ground ellipse.
 *
 * Feed it to `ctx.transform(a, b, c, d, 0, 0)` and then draw at radius r: the result
 * is an ellipse r wide along the cell's own x axis, foreshortened and sheared exactly
 * as that cell is. The x axis is normalised, so r stays in the same units the caller
 * already sizes things in — a fraction of the plate's width.
 */
export interface GroundBasis {
  a: number;
  b: number;
  c: number;
  d: number;
  /** Length of the cell's x axis in pixels, for sizing what gets drawn. */
  width: number;
  /** Depth as a fraction of width — 1 is square on, lower is more foreshortened. */
  depthRatio: number;
}

export function cellGroundBasis(quad: CellQuad): GroundBasis | null {
  if (!quad) return null;
  const { topLeft, topRight, bottomRight, bottomLeft } = quad;
  const corners = [topLeft, topRight, bottomRight, bottomLeft];
  if (corners.some(p => !p || !Number.isFinite(p.x) || !Number.isFinite(p.y))) {
    return null;
  }

  // Averaged over both opposite edges, which absorbs the cell's slight taper into
  // the parallelogram it almost is.
  const ex = {
    x: (topRight.x - topLeft.x + bottomRight.x - bottomLeft.x) / 2,
    y: (topRight.y - topLeft.y + bottomRight.y - bottomLeft.y) / 2,
  };
  const ey = {
    x: (bottomLeft.x - topLeft.x + bottomRight.x - topRight.x) / 2,
    y: (bottomLeft.y - topLeft.y + bottomRight.y - topRight.y) / 2,
  };

  const width = Math.hypot(ex.x, ex.y);
  const depth = Math.hypot(ey.x, ey.y);
  if (!(width > 0) || !(depth > 0)) return null;

  return {
    a: ex.x / width,
    b: ex.y / width,
    c: ey.x / width,
    d: ey.y / width,
    width,
    depthRatio: depth / width,
  };
}
